// Registro en el back de cada bloqueo por ubicacion ficticia (TKT-2149).
//
// El back guarda el log, resuelve a quien avisar (jerarquia + rol adicional) y encola la campanita
// y el correo; el front solo reporta. El reporte es best-effort y NUNCA frena el bloqueo: el aviso
// al usuario y el BINGEO-04 no esperan a la red, y un fallo del POST no se le muestra.

// Services
import DeviceInfoService from '@/services/deviceInfo'
import MockLocationLogService from '@/services/mockLocationLog'

export const MOCK_LOG_MODULES = {
  forms: 'forms',
  exhibitions: 'exhibitions'
}

// Clave que el Excel del back traduce a "GPS simulado (mock provider)".
export const DETECTION_SOURCE = 'mock_provider'

// Mismo margen que el back (60 s por usuario): dentro de el, el back descarta el repetido, asi que
// no vale la pena volver a mandarlo. Solo corre tras un envio exitoso, para no perder un intento
// cuyo primer POST fallo.
export const REPORT_WINDOW_MS = 60000

const EXHIBITIONS_ROUTE = 'navigation.exhibitions.new'
const OCCURRENCE_FILL_ROUTE = 'navigation.occurrence_fill'

const UNKNOWN = 'unknown'

let lastReportedAt = 0
let inFlight = null
// Sube en cada logout: un envio que sale antes y responde despues pertenece a la sesion anterior y
// no debe tocar el margen ni el inFlight del siguiente usuario.
let session = 0

function toId (value) {
  const id = Number(value)

  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * Contexto del bloqueo a partir de la ruta: exhibiciones o formularios, y la ocurrencia cuando se
 * esta llenando una. Con la ocurrencia el back sabe rol y ubicacion exactos; sin ella usa las
 * asignaciones del usuario.
 */
export function mockContextFromRoute (route) {
  return {
    module: route?.name === EXHIBITIONS_ROUTE ? MOCK_LOG_MODULES.exhibitions : MOCK_LOG_MODULES.forms,
    occurrence_id: route?.name === OCCURRENCE_FILL_ROUTE ? toId(route.params?.id) : null
  }
}

export function buildMockLogPayload (position, context = {}, device = null, now = new Date()) {
  const coords = position?.coords ?? {}

  return {
    module: context.module || MOCK_LOG_MODULES.forms,
    simulated_lat: coords.latitude,
    simulated_lng: coords.longitude,
    reported_accuracy: coords.accuracy ?? null,
    detection_source: DETECTION_SOURCE,
    // ISO 8601 en UTC: el back lo normaliza con Carbon::parse()->utc().
    detected_at: now.toISOString(),
    // Sin role_id: el rol lo resuelve el back (el de la ocurrencia o el primer rol del usuario).
    occurrence_id: toId(context.occurrence_id),
    device_name: device?.device_name || UNKNOWN,
    device_model: device?.device_model || UNKNOWN,
    app_version: device?.app_version || UNKNOWN,
    os_name: device?.os_name || UNKNOWN,
    os_version: device?.os_version ?? null
  }
}

async function send (position, context) {
  const sentInSession = session
  // El back cuenta sus 60 s desde que recibe el POST, no desde que responde: el margen se mide desde
  // la salida para no descartar de mas cuando la red es lenta.
  const startedAt = Date.now()

  try {
    const device = await DeviceInfoService.collect()
    await MockLocationLogService.record(buildMockLogPayload(position, context, device))
    if (sentInSession === session) lastReportedAt = startedAt

    return true
  } catch (error) {
    console.error('[mock-location-log] no se pudo registrar la deteccion', error)

    return false
  } finally {
    if (sentInSession === session) inFlight = null
  }
}

/**
 * Manda la deteccion al back. Nunca rechaza. Sin coordenadas no hay nada que registrar (el back
 * las exige); un segundo aviso del mismo intento (guard + componente) se descarta.
 */
export function reportMockDetection (position, context = {}) {
  const coords = position?.coords
  if (coords?.latitude == null || coords?.longitude == null) return Promise.resolve(false)
  if (inFlight) return inFlight
  if (Date.now() - lastReportedAt < REPORT_WINDOW_MS) return Promise.resolve(false)

  inFlight = send(position, context)

  return inFlight
}

// Al cerrar sesion: el margen es por usuario en el back, el siguiente usuario no debe heredarlo.
export function resetMockReportThrottle () {
  session += 1
  lastReportedAt = 0
  inFlight = null
}
